import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input } from '@angular/core';

import { AuiIcon, AuiIconSize, AuiIconWeight } from '@ral/utils:core/ui';
import { FaIconName } from '../models/names';
import { injectAuiIconConfig } from '@ral/utils:core/ui';

@Component({
    selector: 'aui-icon',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [],

    styles: [
        `
            .mask-icon {
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                display: var(--fa-display, inline-block);
                font-family: var(--_fa-family);
                font-feature-settings: normal;
                font-style: normal;
                font-synthesis: none;
                font-variant: normal;
                font-weight: var(--fa-style, 900);
                line-height: 1;
                text-align: center;
                text-rendering: auto;
                width: var(--fa-width, 1.25em);
                height: var(--fa-width, 1.25em);
                display: block;
            }
        `,
    ],
    template: `
        @if (useAssets()) {
            <i
                class="mask-icon {{ calculatedWeight() }}"
                [style.mask-image]="'url(' + baseAssetsPath() + calculatedWeightPath() + ')'"
                [style.-webkit-mask-image]="'url(' + baseAssetsPath() + calculatedWeightPath() + ')'"
                >&nbsp;
            </i>
        } @else {
            <i [class]="calculatedClasses()" [class.fa-fw]="fixedWidth()" data-testid="aui-icon"></i>
        }
    `,
    host: {
        '[class.inline-block]': 'true',
    },
})
export class AuiIconComponent {
    icon = input.required<AuiIcon>();
    weight = input<AuiIconWeight | 'fa-kit' | null>('far');
    size = input<AuiIconSize>();

    /**
     * fixed width https://fontawesome.com/docs/web/style/fixed-width
     *  */
    fixedWidth = input<boolean>(false);

    calculatedWeight = computed(() => {
        const weight = this.weight();
        const icon = this.icon();
        return FaIconName.includes(icon) ? weight : 'fa-kit';
    });

    config = injectAuiIconConfig({ optional: true });
    useAssets = computed(() => {
        return this.config?.useAssets() ?? false;
    });

    baseAssetsPath = computed(() => '/assets/icons');

    calculatedWeightPath = computed(() => {
        const weight = this.weight();
        const icon = this.icon();
        switch (weight) {
            case 'fa-kit':
                return '/custom-icons/' + icon + '.svg';
            case 'fa-brands':
                return '/brands/' + icon + '.svg';
            case 'fas':
                return '/solid/' + icon + '.svg';
            case 'fal':
                return '/light/' + icon + '.svg';
            case 'far':
                return '/regular/' + icon + '.svg';
            default:
                return '/regular/' + icon + '.svg';
        }
        return weight === 'fa-kit' ? '/regular/' + icon + '.svg' : '/brands/' + icon + '.svg';
    });

    calculatedClasses = computed(() => {
        const calculatedWeight = this.calculatedWeight();
        const size = this.size();
        const icon = this.icon();

        const tailwindClasses: string[] = [];
        tailwindClasses.push('fa-' + icon);

        if (calculatedWeight) {
            tailwindClasses.push(calculatedWeight);
        }

        if (size) {
            tailwindClasses.push('fa-' + size);
        }

        return tailwindClasses.join(' ');
    });
}
